/**
 * ArrayInputField Component
 *
 * Reusable string-array input: a labelled text box with Enter-to-add and an Add
 * button, plus a removable chip list of the current items. Owns its own input
 * text state; the parent owns the items array via `onAdd` / `onRemove`.
 */

import { useState } from 'react';
import '@tylertech/forge/button';
import './ArrayInputField.css';

export interface ArrayInputFieldProps {
  /** Field label */
  label: string;
  /** Current items */
  items: string[];
  /** Called with the trimmed value when the user adds an entry */
  onAdd: (value: string) => void;
  /** Called with the index to remove */
  onRemove: (index: number) => void;
  /** Input placeholder */
  placeholder?: string;
  /** Disable the input and Add button */
  disabled?: boolean;
  /** Accessible label for each chip's remove button */
  removeAriaLabel?: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

/**
 * Add/remove chip input for string arrays.
 *
 * @example
 * ```tsx
 * <ArrayInputField
 *   label="Team Member IDs"
 *   items={memberIds}
 *   onAdd={(v) => setValue('memberIds', [...memberIds, v])}
 *   onRemove={(i) => setValue('memberIds', memberIds.filter((_, idx) => idx !== i))}
 *   placeholder="Enter member user ID and press Enter"
 *   removeAriaLabel="Remove member"
 * />
 * ```
 */
export function ArrayInputField({
  label,
  items,
  onAdd,
  onRemove,
  placeholder,
  disabled = false,
  removeAriaLabel = 'Remove item',
}: ArrayInputFieldProps): JSX.Element {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = (): void => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      onAdd(trimmed);
      setInputValue('');
    }
  };

  return (
    <div className="array-field">
      <label className="forge-typography--label1">{label}</label>
      <div className="array-input-group">
        <input
          type="text"
          className="array-input"
          value={inputValue}
          disabled={disabled}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={placeholder}
        />
        <ForgeButton
          type="button"
          variant="outlined"
          className="array-add-button"
          disabled={disabled}
          onClick={handleAdd}
        >
          Add
        </ForgeButton>
      </div>
      {items.length > 0 && (
        <div className="chip-list">
          {items.map((item, index) => (
            <div key={index} className="chip">
              <span className="chip-text">{item}</span>
              <button
                type="button"
                className="chip-remove"
                onClick={() => onRemove(index)}
                aria-label={removeAriaLabel}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
